import { Component, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { CrmNewOrganizationWizardDialogStore } from './crm-new-organization-wizard-dialog.store';
import { FilterBuilder } from '../../../../content/helper/filter.builder';
import { fb } from '../../../../shared/shared.global-service';
import { contentGlobalSuccess } from '../../../../content/content.error';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { getUsersForNewChcrmFrag } from './crm-new-organization-wizard-dialog.query';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { UserGridItemObject } from '@it2go/types';
import { RadioButton } from '@openng/optimus-ui/radiobutton';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { v4 as createUuid } from 'uuid';
import { CollectionCreateInput } from '@libs/types/src/chcrm/input-type/collection/collection-create.input';
import {
    CreateOrganizationPageStore
} from '../../../../content/pages/organization/create-organization-page/create-organization-page.store';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-crm-new-organization-wizard-dialog',
    templateUrl: './crm-new-organization-wizard-dialog.component.html',
    styleUrl: './crm-new-organization-wizard-dialog.component.scss',
    imports: [
        FormsModule,
        EmailInputComponent,
        ReactiveFormsModule,
        RadioButton,
        TranslatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        CrmNewOrganizationWizardDialogStore,
        CreateOrganizationPageStore,
    ],
})
export class CrmNewOrganizationWizardDialogComponent extends BaseDialogComponent {
    public readonly store = inject(CrmNewOrganizationWizardDialogStore);
    public readonly createOrganizationStore = inject(
        CreateOrganizationPageStore,
    );

    protected organizationId: number;
    protected orgName: string;

    protected selectedUser: UserGridItemObject | null = null;

    protected form = fb.group({
        email: fb.control<string | null>(null, [Validators.email]),
    });

    protected loadingState = {
        currentUser: 'init',
        selectedUser: 'init',
        collectionType: 'init',
        collection: 'init',
    };

    currentUser = computed(() =>
        this.store.globalGlobalStore.loggedUserStore.currentUser(),
    );

    emailChange = toSignal(this.form.controls.email.valueChanges);

    foundUsers = computed<UserGridItemObject[]>(
        () => (this.emailChange() && this.store.data.getUsers()?.items) || [],
    );

    constructor() {
        super();
        this.organizationId = this.dialogConfig.data.organizationId;
        this.orgName = this.dialogConfig.data.orgName;

        effect(() => {
            this.emailChange() &&
                this.store.api.getUsers(
                    FilterBuilder.filter({ email: this.emailChange() }),
                    getUsersForNewChcrmFrag,
                );
        });
    }

    override async save() {
        if (!this.organizationId) return;

        this.loadingState.currentUser = 'loading';
        const currentRes = await this.createOrganizationStore.userAssignRulesAndAccess(
            Number(this.organizationId),
            this.currentUser()!.id,
        );
        this.loadingState.currentUser = currentRes ? 'loaded' : 'error';

        if (this.emailChange() && this.selectedUser) {
            this.loadingState.selectedUser = 'loading';
            const selectedRes = await this.createOrganizationStore.userAssignRulesAndAccess(
                Number(this.organizationId),
                this.selectedUser.id,
            );
            this.loadingState.selectedUser = selectedRes ? 'loaded' : 'error';
        }

        if (this.orgName) {
            this.loadingState.collectionType = 'loading';

            const data = {
                categories: [
                    {
                        id: createUuid(),
                        name: this.orgName,
                        threshold: 'regular',
                        exclusive: true,
                        priority: 1,
                    },
                ],
            };
            const settingRes = await this.saveSetting(
                OrganizationSettingTypeEnum.COLLECTION_CATEGORY,
                data,
            );
            this.loadingState.collectionType = settingRes?.id ? 'loaded' : 'error';

            this.loadingState.collection = 'loading';

            const defaultCollectionData: CollectionCreateInput = {
                name: this.orgName,
                organizationId: Number(this.organizationId),
                categoryId: data.categories[0].id,
                active: true,
                longTerm: true,
                comment: 'Default collection',
                variableSymbols: [''],
                darujmePledgeIds: [''],
            };
            const res = await this.store.api.createCollection(defaultCollectionData);
            this.loadingState.collection = res ? 'loaded' : 'error';
        }

        if (Object.values(this.loadingState).some(s => s === 'error')) {
            return;
        }

        contentGlobalSuccess.next('common.updated');
        this.dialogRef.close(true);
    }

    private async saveSetting(key: string, data: any) {
        return await this.store.api.updateOrganizationSetting({
            key: key,
            organizationId: this.organizationId,
            data: JSON.stringify(data),
        });
    }
}

